/*
 * <twc-gift-packs> — GIFT NOW on the gift-pack cards
 * (sections/twc-gift-packs.liquid; Figma "Gifting" board).
 *
 * GIFT NOW is a <button data-twc-gift-buy data-variant-id>. One delegated
 * click on the root reads the card's quantity dropdown ([data-twc-gift-qty],
 * cap 10) and runs the clear-first cart write, then sends the buyer to
 * checkout — the theme has no cart UI (an isolated flow).
 *
 * CLEAR-FIRST. Every gift entry point reaches checkout with an empty cart
 * behind it, so a member can never check out a mixed cart. The sequence is
 * TwcCartAttributes.clearThenAdd() (assets/twc-cart-attributes.js), the same
 * one the join and gift-card flows run:
 *   1. POST /cart/clear.js
 *   2. POST /cart/update.js { attributes: { purchase_type: 'gift' } }
 *      — merged, never assigned. The clear removes the LINE ITEMS only and
 *      keeps the cart attributes, so the gift marker still needs its own
 *      write.
 *   3. POST /cart/add.js { items: [{ id, quantity }] }
 *   4. location.href = '/checkout'
 * FATAL at every step: a failure shows the card's error line and does NOT
 * redirect. The section loads twc-cart-attributes.js with `defer` ahead of
 * this file, which keeps the order — a missing helper is a build fault, and
 * the click fails closed rather than adding to a cart it never cleared.
 *
 * Busy state: the button is disabled with aria-busy and shows its pending
 * label until the navigation. pageshow(persisted) releases it when the buyer
 * comes back through the back-forward cache.
 *
 * <noscript> in the block keeps a cart-permalink anchor for a visitor without
 * script; the section's stylesheet hides the button until this element is
 * defined. The card's "Read more" is a plain link to the product page.
 *
 * One element per section, two listeners on one AbortController (house law:
 * connectedCallback MUST have disconnectedCallback cleanup). No engine calls,
 * no member state — the member fork is settled in Liquid.
 */
(function (global) {
  'use strict';

  if (global.customElements && global.customElements.get('twc-gift-packs')) return;

  var FALLBACK_ERROR = 'Something went wrong. Please try again.';

  function routeRoot() {
    return (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
  }

  // The pending label rides the button as data-pending-label; the original
  // label is parked on the dataset while the write runs.
  function setBusy(button, on) {
    button.disabled = !!on;
    button.setAttribute('aria-busy', on ? 'true' : 'false');
    if (on) {
      button.dataset.twcLabel = button.textContent;
      button.textContent = button.getAttribute('data-pending-label') || button.textContent;
    } else if (button.dataset.twcLabel) {
      button.textContent = button.dataset.twcLabel;
      delete button.dataset.twcLabel;
    }
  }

  function errorLine(card) {
    return card.querySelector('[data-twc-gift-error]');
  }

  function showError(card, message) {
    var line = errorLine(card);
    if (!line) return;
    line.textContent = message || '';
    line.hidden = !message;
  }

  function genericError(card) {
    var line = errorLine(card);
    return (line && line.getAttribute('data-generic')) || FALLBACK_ERROR;
  }

  class TwcGiftPacks extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      var signal = this.controller.signal;
      var root = this;

      root.addEventListener(
        'click',
        function (event) {
          var button = event.target.closest('[data-twc-gift-buy]');
          if (!button || button.disabled) return;
          var card = button.closest('article');
          if (!card) return;
          root.buy(button, card);
        },
        { signal: signal }
      );

      // Back-forward cache: returning from checkout restores the frozen page
      // — release every pending button so the cards are usable again.
      global.addEventListener(
        'pageshow',
        function (event) {
          if (!event.persisted) return;
          root.querySelectorAll('[data-twc-gift-buy][aria-busy="true"]').forEach(function (button) {
            setBusy(button, false);
          });
        },
        { signal: signal }
      );
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
    }

    buy(button, card) {
      var variantId = parseInt(button.getAttribute('data-variant-id'), 10);
      if (!(variantId > 0)) {
        showError(card, genericError(card));
        return;
      }

      var qty = card.querySelector('[data-twc-gift-qty]');
      var count = qty ? parseInt(qty.value, 10) : 1;
      if (!(count >= 1)) count = 1;

      // TwcCartAttributes owns the clear-first cart write, so GIFT NOW cannot
      // run without it (see the header).
      var attrs = global.TwcCartAttributes;
      if (!attrs) {
        showError(card, genericError(card));
        return;
      }

      showError(card, '');
      setBusy(button, true);

      // A gift pack is a GIFT order, so purchase_type=gift rides the
      // attribute write that follows the clear. It merges into the body — an
      // assignment to `attributes` would wipe a referral code the visitor
      // brought with them. The order also names the bundle as the `Bundle`
      // attribute — the bundle's title, with " x<count>" (the ASCII letter
      // x) when the buyer takes more than one — the same key and format the
      // engine stamps on the orders it creates, so the order email prints
      // the bundle name in every case.
      var updateBody = {};
      attrs.merge(updateBody, attrs.GIFT);
      var bundleTitle = button.getAttribute('data-bundle-title');
      if (bundleTitle) {
        attrs.merge(updateBody, { Bundle: count > 1 ? bundleTitle + ' x' + count : bundleTitle });
      }

      // FATAL at every step (see the header): the clear is what guarantees
      // the buyer checks out with the gift pack and nothing else.
      // After the add, and only after it: twc:add_to_cart from the add.js
      // body, then the ONE redirect once the bridge has settled (bounded at
      // ~300 ms). trackAdded never rejects, so a successful cart write always
      // reaches checkout; a failed step above skips it, so an error path
      // never publishes.
      attrs
        .clearThenAdd(routeRoot(), updateBody, [{ id: variantId, quantity: count }])
        .then(function (body) {
          return attrs.trackAdded('gift_pack', attrs.addedItems(body));
        })
        .then(function () {
          global.location.href = '/checkout';
          // The pending state holds during navigation; pageshow releases it
          // if the buyer comes straight back.
        })
        .catch(function (err) {
          setBusy(button, false);
          var detail = err && err.data && (err.data.description || err.data.message);
          showError(card, typeof detail === 'string' && detail !== '' ? detail : genericError(card));
        });
    }
  }

  if (global.customElements) {
    global.customElements.define('twc-gift-packs', TwcGiftPacks);
  }
})(window);
